import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { sellerGamificationService } from '../../../services/api/sellerGamification.service';
import type { SellerGamificationDashboard } from '../../../types/sellerGamification.types';
import { logGrowthOsDashboardSnapshot } from './growthOsDevLog';
import { peekCelebrationQueue } from './celebrationQueue';

type SellerGamificationContextValue = {
  data: SellerGamificationDashboard | null;
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  dismissCelebration: (unlockId: string) => Promise<void>;
  dismissAllCelebrations: () => Promise<void>;
};

const SellerGamificationContext = createContext<SellerGamificationContextValue | null>(null);

export function SellerGamificationProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<SellerGamificationDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const location = useLocation();

  const load = useCallback(async () => {
    setError(false);
    try {
      const dashboard = await sellerGamificationService.getDashboard();
      setData(dashboard);
      logGrowthOsDashboardSnapshot(dashboard);
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Refresh when entering seller routes with queued celebrations (e.g. after listing wizard publish)
  useEffect(() => {
    if (!location.pathname.startsWith('/seller')) return;
    if (peekCelebrationQueue().length === 0) return;
    void load();
  }, [location.pathname, load]);

  const dismissCelebration = useCallback(async (unlockId: string) => {
    await sellerGamificationService.dismissCelebration(unlockId);
    setData((prev) =>
      prev
        ? {
            ...prev,
            pendingCelebrations: prev.pendingCelebrations.filter((c) => c.id !== unlockId),
            achievements: prev.achievements.map((a) =>
              a.id === unlockId ? { ...a, celebrationDismissed: true } : a,
            ),
          }
        : prev,
    );
  }, []);

  const dismissAllCelebrations = useCallback(async () => {
    await sellerGamificationService.dismissAllCelebrations();
    setData((prev) =>
      prev
        ? {
            ...prev,
            pendingCelebrations: [],
            achievements: prev.achievements.map((a) => ({
              ...a,
              celebrationDismissed: true,
            })),
          }
        : prev,
    );
  }, []);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      reload: load,
      dismissCelebration,
      dismissAllCelebrations,
    }),
    [data, loading, error, load, dismissCelebration, dismissAllCelebrations],
  );

  return (
    <SellerGamificationContext.Provider value={value}>
      {children}
    </SellerGamificationContext.Provider>
  );
}

export function useSellerGamification() {
  const ctx = useContext(SellerGamificationContext);
  if (!ctx) {
    throw new Error('useSellerGamification must be used within SellerGamificationProvider');
  }
  return ctx;
}

export function useSellerGamificationOptional() {
  return useContext(SellerGamificationContext);
}

/** Call after actions that may unlock achievements (publish, profile save). Returns synced dashboard. */
export async function refreshSellerGamification(
  reload?: () => Promise<void>,
): Promise<SellerGamificationDashboard | null> {
  try {
    const synced = await sellerGamificationService.sync();
    if (reload) {
      await reload();
    }
    return synced;
  } catch {
    toast.error('Could not refresh seller progress');
    return null;
  }
}
